import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaListUl, FaCalendarAlt, FaClipboardList } from 'react-icons/fa';
import { getMenuChannel, DEFAULT_MENU_CHANNEL } from './menuChannels';
import Menu from './MenuTable';
import MenuTemplate from './MenuTemplate';
import CateringTemplate from './CateringTemplate';
import MenuTemplateDetail from './MenuTemplateDetail';
import CateringTemplateDetail from './CateringTemplateDetail';
import FoodItemRecipePage, { RECIPE_TAB_PREFIX, recipeTabPath } from './FoodItemRecipePage';
import FoodItemDetailPage, { FOOD_ITEM_DETAIL_TAB_PREFIX, foodItemDetailTabPath } from './FoodItemDetailPage';
import DailyMenu from './DailyMenu';
import { RESTAURANT_API_PATHS } from '../constants/apiPaths';
import { ROUTES } from '../constants/appRoutes';
import axiosRestaurantInstance from './AxiosRestaurantInstance';
import {
  extractRestaurantId,
  extractRestaurantName,
  fetchUserRestaurants,
  normalizeFoodCategories,
  normalizeAddressesForTable,
  normalizeIsDefaultFormValue,
  resolveInitialRestaurant,
  updateRestaurant,
  updateRestaurantAddress,
} from './restaurantApi';
import {
    createOnlineFoodItem,
    loadGroupedOnlineMenuItems,
    replaceFoodItemAgePricing,
    resolveIngredientIdsFromMenuItem,
    updateOnlineFoodItem,
    EMPTY_GROUP_SCALE_FIELDS,
} from './onlineFoodItems';
import { parseAgePricingFromItem } from './AgePricingEditor';
import { emptyNutritionRows } from './foodNutrition';
import { ensureMenuImageFile } from './menuImageUtils';
import {
    fetchDailyMenuItemsByDate,
    getLocalDateString,
    normalizeTemplateSource,
    parseDailyMenuByDateResponse,
    recordDailyMenuTemplateDate,
    triggerDailyMenuAutoPublish,
    resolveDailyMenuRowId,
    dailyMenuRowIdsEqual,
    markDailyMenuSkipAutoPublish,
    shouldSkipDailyMenuAutoPublish,
    buildDailyMenuAutoPublishKey,
} from './dailyMenuApi';
import Modal from './Modal';
import { confirmAction, showUndoToast } from './ui/feedback';
import './css/Restaurants.css'; 
import './css/MenuNav.css';
// 

const RESTAURANTS_HOME_TAB = 'restaurants';
const MY_TEMPLATES_HOME_TAB = 'restaurants/mytemplates';

/** Keep Menu sidebar channel in the URL so Jr Eats / Dine-In do not reset to My Eats on navigate.
 * Use businessAdmin (not /home) so Home is not remounted via PreserveSearchRedirect. */
function restaurantsHomeUrl(tab, channel) {
    const params = new URLSearchParams();
    params.set('tab', tab || RESTAURANTS_HOME_TAB);
    if (channel) params.set('channel', channel);
    return `${ROUTES.businessAdmin}?${params.toString()}`;
}

function parseRestaurantTabFromUrl(tabRaw) {
    const tab = (tabRaw || '').toLowerCase();
    if (tab === MY_TEMPLATES_HOME_TAB) {
        return { menuSubView: 'myTemplates', templateId: null, foodItemId: null, recipeMode: 'view' };
    }
    if (tab.startsWith(`${MY_TEMPLATES_HOME_TAB}/`)) {
        const templateId = tab.slice(MY_TEMPLATES_HOME_TAB.length + 1).split('/')[0];
        if (templateId) {
            return { menuSubView: 'templateDetail', templateId, foodItemId: null, recipeMode: 'view' };
        }
        return { menuSubView: 'myTemplates', templateId: null, foodItemId: null, recipeMode: 'view' };
    }
    if (tab.startsWith(`${FOOD_ITEM_DETAIL_TAB_PREFIX}/`)) {
        const foodItemId = tab.slice(FOOD_ITEM_DETAIL_TAB_PREFIX.length + 1).split('/')[0];
        if (foodItemId) {
            return { menuSubView: 'foodItemDetail', templateId: null, foodItemId, recipeMode: 'view' };
        }
    }
    if (tab === RECIPE_TAB_PREFIX || tab.startsWith(`${RECIPE_TAB_PREFIX}/`)) {
        const rest =
            tab === RECIPE_TAB_PREFIX ? '' : tab.slice(RECIPE_TAB_PREFIX.length + 1);
        const segments = rest.split('/').filter(Boolean);
        const foodItemId = segments[0] || null;
        let recipeMode = 'view';
        if (segments[1] === 'add') recipeMode = 'add';
        else if (segments[1] === 'edit') recipeMode = 'edit';
        return { menuSubView: 'recipe', templateId: null, foodItemId, recipeMode };
    }
    return { menuSubView: 'menu', templateId: null, foodItemId: null, recipeMode: 'view' };
}

function getMenuSubViewFromUrl() {
    return parseRestaurantTabFromUrl(
        new URLSearchParams(window.location.search).get('tab')
    ).menuSubView;
}

const SupRestaurant = ({ menuChannel = DEFAULT_MENU_CHANNEL }) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [restaurantName, setRestaurantName] = useState('');
    const [restaurantShortCode, setRestaurantShortCode] = useState('');
    const [contactNo, setContactNo] = useState('');
    const [email, setEmail] = useState('');
    const [cuisineType, setCuisineType] = useState('');
    const [notes, setNotes] = useState('');
    const [status, setStatus] = useState('');
    const [isDefault, setIsDefault] =useState('');
    const [addresses, setAddresses] = useState([]);
    const [activeTab, setActiveTab] = useState('dailyMenu');
    const [menuSubView, setMenuSubView] = useState(() => getMenuSubViewFromUrl());
    const [selectedTemplateId, setSelectedTemplateId] = useState(
        () => parseRestaurantTabFromUrl(new URLSearchParams(window.location.search).get('tab')).templateId
    );
    const [selectedRecipeFoodItemId, setSelectedRecipeFoodItemId] = useState(
        () => parseRestaurantTabFromUrl(new URLSearchParams(window.location.search).get('tab')).foodItemId
    );
    const [recipePageMode, setRecipePageMode] = useState(
        () => parseRestaurantTabFromUrl(new URLSearchParams(window.location.search).get('tab')).recipeMode
    );
    const activeChannel = getMenuChannel(menuChannel);
    const menuSalesChannel =
        menuChannel === 'dinein' ? 'dinein' : menuChannel === 'myeats' ? 'online' : '';
    const [menuTypeFilter, setMenuTypeFilter] = useState(activeChannel.type);
    const [menuListSource, setMenuListSource] = useState(activeChannel.source);
    const [dailyMenuListSource, setDailyMenuListSource] = useState(activeChannel.source);
    const dailyMenuAutoPublishAttemptedRef = useRef(new Set());
    const [restaurantId, setRestaurantId] = useState(null);
    const [selectedRestaurantForMenu, setSelectedRestaurantForMenu] = useState(null);

    const reloadOnlineMenuItems = useCallback(
        async (rid) => {
            const id = rid ?? selectedRestaurantForMenu ?? restaurantId;
            if (!id) return;
            try {
                const { items } = await loadGroupedOnlineMenuItems(id, menuListSource, {
                    ...(menuSalesChannel ? { salesChannel: menuSalesChannel } : {}),
                });
                setMenuItems(items);
            } catch (err) {
                console.error('Error reloading online menu:', err?.response?.data || err);
                setMenuItems({});
            }
        },
        [menuListSource, menuSalesChannel, restaurantId, selectedRestaurantForMenu]
    );
    const [menuItems, setMenuItems] = useState({});
    const [cateringMenuItems, setCateringMenuItems] = useState({});
    const [foodCategories, setFoodCategories] = useState([]);
    const [showMenuTable, setShowMenuTable] = useState(false); // Manage table display
    const [addedDailyMenuItems, setAddedDailyMenuItems] = useState([]);
    const [dailyMenuItems, setDailyMenuItems] = useState([]);
    const [dailyMenuViewDate, setDailyMenuViewDate] = useState(() => getLocalDateString());
    const [editingItemId, setEditingItemId] = useState(null); 
    const [tempDailyMenuItem, setTempDailyMenuItem] = useState({});
    const [currentPageDailyMenu, setCurrentPageDailyMenu] = useState(1); // Pagination for Daily Menu
    const [itemsPerPageDailyMenu] = useState(10); // Items per page for daily menu
    const [isEditingRestaurant, setIsEditingRestaurant] = useState(false);
    const [restaurantRecord, setRestaurantRecord] = useState(null);
    const [restaurants, setRestaurants] = useState([]);

        // Retrieve user from localStorage
    const user = JSON.parse(localStorage.getItem('user'));
    // const userId = user ? user.userId : null;

    
    // State for adding a new menu item
    const [showAddMenu, setShowAddMenu] = useState(false);
    const [newMenuItem, setNewMenuItem] = useState({
        foodType: '',
        foodCategoryId: '',
        itemFoodType: 'VEG',
        foodItem: '',
        price: '',
        quantity: '',
        notes: '',
        ingredients: '',
        ingredientIds: [],
        cuisineOrigin: '',
        description: '',
        currency: 'INR',
        nutrition: emptyNutritionRows(),
        menuSource: 'MYEATS',
        availableForDineIn: true,
        availableForOnline: true,
        menuType: 'ONLINE',
        isAvailable: true,
        imageFile: null,
        agePricing: [],
        ...EMPTY_GROUP_SCALE_FIELDS,
    });

    const handleUpdateRestaurant = async () => {
        if (!restaurantId) {
            alert("No restaurant selected");
            return;
        }

        try {
            const result = await updateRestaurant(
                restaurantId,
                {
                    restaurantName,
                    restaurantShortCode,
                    contactNo,
                    email,
                    cuisineType,
                    notes,
                    isDefault,
                    avgPrepTime:
                        restaurantRecord?.avgPrepTime ?? restaurantRecord?.AvgPrepTime,
                    avgDeliveryTimePerKm:
                        restaurantRecord?.avgDeliveryTimePerKm ??
                        restaurantRecord?.AvgDeliveryTimePerKm,
                    imageUrl:
                        restaurantRecord?.imageUrl ??
                        restaurantRecord?.image ??
                        restaurantRecord?.Image,
                    primaryAddress:
                        Array.isArray(addresses) && addresses.length > 0 ? addresses[0] : null,
                },
                user?.userId ?? 1
            );

            alert(result?.message || "Restaurant updated successfully");
            setIsEditingRestaurant(false);
        } catch (error) {
            console.error("Update restaurant error:", error);
            alert(error?.response?.data?.message || "Failed to update restaurant");
        }
    };

const handleDeleteRestaurant = async () => {
    if (!restaurantId) return;

    const confirmDelete = await confirmAction({
        title: 'Delete restaurant?',
        message: 'Are you sure you want to delete this restaurant? This action cannot be undone.',
        confirmLabel: 'Delete',
        danger: true,
    });

    if (!confirmDelete) return;

    try {
        const token = localStorage.getItem('Token');

        const response = await axiosRestaurantInstance.delete(
            RESTAURANT_API_PATHS.restaurantDeactivate(restaurantId),
            {
                headers: {
                    Authorization: `Bearer ${token}`
                }
            }
        );

        if (response.status === 200) {
            alert("Restaurant deleted successfully");

            // Clear all state
            setRestaurantId(null);
            setRestaurantName('');
            setRestaurantShortCode('');
            setContactNo('');
            setEmail('');
            setCuisineType('');
            setStatus('');
            setIsDefault('');
            setNotes('');
            setAddresses([]);
            setMenuItems({});
            setDailyMenuItems([]);
            setIsEditingRestaurant(false);
        }
    } catch (error) {
        console.error("Delete restaurant error:", error);
        alert("Failed to delete restaurant");
    }
};

//--------------------------------- Restaurant Address --------------------------------------------------------
const handleAddressUpdate = async (index, updatedAddress) => {
  if (!updatedAddress.addressId) {
    alert("Cannot update: missing address_id");
    return;
  }

  try {
    const updatedById = user?.userId ?? 1;
    await updateRestaurantAddress(updatedAddress.addressId, updatedAddress, updatedById);

    const updated = [...addresses];
    updated[index] = updatedAddress;
    setAddresses(updated);
    alert('Address updated successfully!');
  } catch (error) {
    console.error('Update error:', error);
    alert(error?.response?.data?.message || 'An error occurred while updating address.');
  }
};


//   const handleAddressDelete = (index) => {
//     const updated = addresses.filter((_, i) => i !== index);
//     setAddresses(updated);
//   };
        
//--------------------------------- DailyMenu API--------------------------------------------------------

const postDailyMenuItemEntry = async (fields, addOptions = {}) => {
    const token = localStorage.getItem('Token');
    if (!token) {
        const err = new Error('Session expired or you are not signed in. Please sign in again.');
        err.code = 'NO_TOKEN';
        throw err;
    }

    const foodItemIdNum = Number(fields.foodItemId ?? fields.Food_Item_Id);
    if (!Number.isFinite(foodItemIdNum)) {
        const err = new Error('Missing food item id.');
        err.code = 'INVALID_FOOD_ITEM_ID';
        throw err;
    }

    const availability = fields.no_of_items_available ?? fields.No_of_items_available;
    const noOfItemsAvailable =
        availability !== '' && availability != null && !Number.isNaN(Number(availability))
            ? Number(availability)
            : 0;

    const menuDate = String(
        addOptions.menuDate ?? dailyMenuViewDate ?? getLocalDateString()
    ).trim();
    const menuSource = normalizeTemplateSource(
        addOptions.menuSource ?? addOptions.Menu_Source ?? 'MYEATS'
    );

    const newItemData = {
        Restaurant_Id: restaurantId,
        Food_Type: fields.foodType ?? fields.Food_Type ?? '',
        Food_Item_Id: foodItemIdNum,
        Price: String(fields.price ?? fields.Price ?? '').trim(),
        Notes: fields.notes ?? fields.Notes ?? '',
        Meal: fields.meal ?? fields.Meal ?? '',
        No_of_items_available: noOfItemsAvailable,
        Created_By_Id: user?.userId || restaurantId,
        menuDate,
        Menu_Date: menuDate,
        menuSource,
        Menu_Source: menuSource,
    };

    return axiosRestaurantInstance.post(RESTAURANT_API_PATHS.restaurantsDailyMenuItems, newItemData, {
        headers: { Authorization: `Bearer ${token}` },
    });
};

const handleBulkAddDailyMenuItems = async (items, addOptions = {}) => {
    if (menuTypeFilter !== 'online') {
        alert('Switch to Online in the menu type dropdown to add daily menu items.');
        return { ok: false, added: 0, failed: 0 };
    }

    const rows = Array.isArray(items) ? items : [];
    if (rows.length === 0) {
        alert('Select at least one menu item.');
        return { ok: false, added: 0, failed: 0 };
    }

    let added = 0;
    let failed = 0;
    let lastError = '';

    for (const row of rows) {
        try {
            const response = await postDailyMenuItemEntry(row, addOptions);
            if (response.status === 200 || response.status === 201) {
                added += 1;
            } else {
                failed += 1;
                lastError = `Server responded with status ${response.status}`;
            }
        } catch (error) {
            failed += 1;
            lastError =
                error.response?.data?.message ||
                error.response?.data?.error ||
                error.message ||
                'Unknown error';
            if (error.response?.status === 401 || error.code === 'NO_TOKEN') {
                alert('Session expired or unauthorized. Please sign in again.');
                break;
            }
        }
    }

    if (added > 0) {
        try {
            await loadDailyMenuItems(dailyMenuViewDate);
        } catch (error) {
            console.error('Error reloading daily menu items:', error);
        }
    }

    return { ok: failed === 0, added, failed, lastError };
};

const closeAddItemModal = () => {
    setShowMenuTable(false);
};
    // State for editing menu items
    const [editingItem, setEditingItem] = useState(null);

    // GET /catering/menu/:restaurantId → { items: [ { cateringItemId, foodType, itemName, price, quantity, imagePath, imageUrl, ... } ] }
    const parseCateringMenuResponse = (data) => {
        if (!data || typeof data !== 'object') return [];
        if (data.items && Array.isArray(data.items)) return data.items;
        if (data.cateringItemId != null) return [data];
        if (Array.isArray(data)) return data;
        return typeof data === 'object' ? Object.values(data).flat() : [];
    };

    // Build display URL from imagePath (API may return path only; images served at origin/uploads/path)
    const getDailyMenuImageUrl = (imagePath) => {
        if (!imagePath || typeof imagePath !== 'string') return null;
        const path = String(imagePath).trim().replace(/%22|"$/g, '').replace(/^\//, '');
        if (!path) return null;
        if (path.startsWith('http://') || path.startsWith('https://')) return path.replace(/%22|"$/g, '');
        const base = axiosRestaurantInstance.defaults.baseURL || process.env.REACT_APP_API_COMPANY_URL || '';
        if (!base) return null;
        try {
            return `${new URL(base).origin}/uploads/${path}`;
        } catch (e) {
            return `${base.replace(/\/$/, '')}/uploads/${path}`;
        }
    };

    const mapCateringMenuItem = (item) => {
        const imagePath = item.imagePath != null && String(item.imagePath).trim() ? item.imagePath : null;
        const rawImageUrl = item.imageUrl != null && String(item.imageUrl).trim() ? item.imageUrl : null;
        const imageUrl = rawImageUrl
            ? String(rawImageUrl).trim().replace(/^%22+|%22+$/g, '').replace(/^"+|"+$/g, '').trim()
            : (imagePath ? getDailyMenuImageUrl(imagePath) : null);
        return {
            Food_Item_Id: item.cateringItemId ?? item.Catering_Item_Id ?? item.Food_Item_Id,
            cateringItemId: item.cateringItemId,
            restaurantId: item.restaurantId ?? item.Restaurant_Id,
            foodCategoryId: item.foodCategoryId ?? item.food_category_id ?? item.Food_Category_Id,
            foodCategory: item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? '',
            Food_Category: item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? '',
            Food_Type: item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? '',
            itemFoodType: item.foodType ?? item.itemFoodType ?? 'VEG',
            Food_Item: item.itemName ?? item.Item_Name ?? item.item_name ?? item.Food_Item ?? item.foodItem ?? '',
            Price: item.price ?? item.Price,
            Quantity: item.quantity ?? item.servingSize ?? item.Serving_Size ?? item.Quantity ?? 'serves 1',
            Description: item.description ?? item.Description ?? '',
            isAvailable: item.isAvailable ?? item.Is_available ?? item.is_available ?? true,
            imagePath,
            imageUrl: imageUrl || undefined,
        };
    };

    const loadDailyMenuItems = useCallback(
        async (menuDate) => {
            const effectiveRestaurantId = selectedRestaurantForMenu || restaurantId;
            if (!effectiveRestaurantId || menuTypeFilter !== 'online') return;
            const date = (menuDate || dailyMenuViewDate || getLocalDateString()).trim();
            try {
                let data = await fetchDailyMenuItemsByDate(effectiveRestaurantId, date, {
                    menuSource: dailyMenuListSource,
                });
                let rows = parseDailyMenuByDateResponse(data);
                const today = getLocalDateString();
                const autoPublishKey = buildDailyMenuAutoPublishKey(
                    effectiveRestaurantId,
                    date,
                    dailyMenuListSource
                );

                if (rows.length > 0) {
                    markDailyMenuSkipAutoPublish(effectiveRestaurantId, date, dailyMenuListSource);
                }

                if (
                    rows.length === 0 &&
                    date === today &&
                    autoPublishKey &&
                    !dailyMenuAutoPublishAttemptedRef.current.has(autoPublishKey) &&
                    !shouldSkipDailyMenuAutoPublish(effectiveRestaurantId, date, dailyMenuListSource)
                ) {
                    dailyMenuAutoPublishAttemptedRef.current.add(autoPublishKey);
                    try {
                        await triggerDailyMenuAutoPublish({
                            restaurantId: effectiveRestaurantId,
                            menuDate: date,
                        });
                        data = await fetchDailyMenuItemsByDate(effectiveRestaurantId, date, {
                            menuSource: dailyMenuListSource,
                        });
                        rows = parseDailyMenuByDateResponse(data);
                    } catch (publishErr) {
                        console.warn(
                            'Daily menu auto-publish fallback failed:',
                            publishErr?.response?.data || publishErr?.message || publishErr
                        );
                    }
                }
                rows.forEach((item) => {
                    const tid = item.templateId ?? item.template_id;
                    if (tid != null && String(tid).trim() !== '') {
                        recordDailyMenuTemplateDate(tid, date);
                    }
                });
                setDailyMenuItems(rows);
            } catch (err) {
                console.error('Error loading daily menu:', err?.response?.data || err);
                setDailyMenuItems([]);
            }
        },
        [restaurantId, selectedRestaurantForMenu, menuTypeFilter, dailyMenuViewDate, dailyMenuListSource]
    );

    const handleViewMenuDateChange = useCallback(
        async (date) => {
            const d = date && String(date).trim() ? String(date).trim() : getLocalDateString();
            setDailyMenuViewDate(d);
            await loadDailyMenuItems(d);
        },
        [loadDailyMenuItems]
    );

    const applyRestaurantToForm = (restaurant) => {
        setRestaurantRecord(restaurant);
        const id = extractRestaurantId(restaurant);
        const name = extractRestaurantName(restaurant);
        const contact = restaurant.contactNo ?? restaurant.ContactNo ?? '';
        const isDef = restaurant.isDefault ?? restaurant.IsDefault ?? restaurant.is_default;

        setRestaurantId(id);
        setRestaurantName(name);
        setRestaurantShortCode(restaurant.restaurantShortCode ?? restaurant.RestaurantShortCode ?? '');
        setContactNo(contact !== '' && contact != null ? String(contact) : '');
        setEmail(restaurant.email ?? restaurant.Email ?? '');
        setCuisineType(restaurant.cuisineType ?? restaurant.CuisineType ?? '');
        setNotes(restaurant.notes ?? restaurant.Notes ?? '');
        setStatus(restaurant.status ?? restaurant.Status ?? '');
        setIsDefault(normalizeIsDefaultFormValue(isDef));
        setAddresses(normalizeAddressesForTable(restaurant));

        if (id) {
            try {
                localStorage.setItem('restaurantId', String(id));
                if (name) localStorage.setItem('restaurantName', name);
            } catch (e) {
                // ignore storage errors
            }
        }
    };

    const loadRestaurantMenus = async (id) => {
        const token = localStorage.getItem('Token');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
            if (menuTypeFilter === 'catering') {
                const cateringResponse = await axiosRestaurantInstance.get(`/catering/menu/${id}`, { headers });
                const cateringItems = parseCateringMenuResponse(cateringResponse?.data || {});
                const grouped = {};
                cateringItems.forEach((item) => {
                    const cat = item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? item.Food_Type ?? 'Other';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(mapCateringMenuItem(item));
                });
                setCateringMenuItems(grouped);
                setMenuItems({});
            } else {
                const { items } = await loadGroupedOnlineMenuItems(id, menuListSource, {
                    ...(menuSalesChannel ? { salesChannel: menuSalesChannel } : {}),
                });
                setMenuItems(items);
                setCateringMenuItems({});
            }
        } catch (menuErr) {
            console.warn('Could not load menu items:', menuErr?.message || menuErr);
            setMenuItems({});
            setCateringMenuItems({});
        }

        if (menuTypeFilter === 'online') {
            await loadDailyMenuItems(dailyMenuViewDate);
        } else {
            setDailyMenuItems([]);
        }
    };

    const clearRestaurantForm = () => {
        setRestaurantId(null);
        setRestaurantName('');
        setRestaurantShortCode('');
        setContactNo('');
        setEmail('');
        setCuisineType('');
        setNotes('');
        setStatus('');
        setIsDefault('');
        setAddresses([]);
        try {
            localStorage.removeItem('restaurantId');
            localStorage.removeItem('restaurantName');
        } catch (e) {
            // ignore storage errors
        }
    };

    // Load user's restaurants first, then resolve default (avoids race with empty list)
    useEffect(() => {
        const initRestaurant = async () => {
            const userId = user?.userId;
            if (!userId) return;

            try {
                const list = await fetchUserRestaurants(userId);
                setRestaurants(list);

                const restaurant = await resolveInitialRestaurant(userId, list);
                if (restaurant) {
                    applyRestaurantToForm(restaurant);
                    const id = extractRestaurantId(restaurant);
                    if (id) await loadRestaurantMenus(id);
                    return;
                }

                console.info('No restaurant found for user yet.');
                clearRestaurantForm();
            } catch (error) {
                console.error('Error loading restaurant:', error);
            }
        };

        initRestaurant();
    }, []);

    // Fetch menu items when restaurantId or menu type changes
    useEffect(() => {
        const fetchMenuByType = async () => {
            const effectiveRestaurantId = menuTypeFilter === 'online'
                ? (selectedRestaurantForMenu || restaurantId)
                : restaurantId;
            if (!effectiveRestaurantId) {
                if (menuTypeFilter === 'online') {
                    setMenuItems({});
                    setDailyMenuItems([]);
                }
                return;
            }

            const token = localStorage.getItem('Token');
            const headers = token ? { Authorization: `Bearer ${token}` } : {};

            try {
                if (menuTypeFilter === 'online') {
                    const { items } = await loadGroupedOnlineMenuItems(
                        effectiveRestaurantId,
                        menuListSource,
                        menuSalesChannel ? { salesChannel: menuSalesChannel } : {}
                    );
                    setMenuItems(items);
                } else if (menuTypeFilter === 'catering') {
                    setMenuItems({});
                    try {
                        const cateringResponse = await axiosRestaurantInstance.get(`/catering/menu/${effectiveRestaurantId}`, { headers });
                        const cateringData = cateringResponse.data || {};
                        const cateringItems = parseCateringMenuResponse(cateringData);
                        const grouped = {};
                        cateringItems.forEach((item) => {
                            const cat = item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? item.Food_Type ?? 'Other';
                            if (!grouped[cat]) grouped[cat] = [];
                            grouped[cat].push(mapCateringMenuItem(item));
                        });
                        setCateringMenuItems(grouped);
                    } catch (cateringErr) {
                        console.error('Error fetching catering menu:', cateringErr);
                        setCateringMenuItems({});
                    }
                }
            } catch (error) {
                console.error('Error fetching menu items:', error);
            }
        };

        fetchMenuByType();
    }, [restaurantId, menuTypeFilter, selectedRestaurantForMenu, menuListSource, menuSalesChannel]);

    useEffect(() => {
        if (menuTypeFilter !== 'online') {
            setDailyMenuItems([]);
            return;
        }
        const effectiveRestaurantId = selectedRestaurantForMenu || restaurantId;
        if (!effectiveRestaurantId) {
            setDailyMenuItems([]);
            return;
        }
        loadDailyMenuItems(dailyMenuViewDate);
    }, [
        dailyMenuViewDate,
        restaurantId,
        selectedRestaurantForMenu,
        menuTypeFilter,
        dailyMenuListSource,
        loadDailyMenuItems,
    ]);

    useEffect(() => {
        const onTemplateSynced = (e) => {
            if (activeTab !== 'dailyMenu' || menuTypeFilter !== 'online') return;
            const dates = e.detail?.menuDates;
            const view = dailyMenuViewDate;
            if (
                Array.isArray(dates) &&
                dates.length > 0 &&
                !dates.includes(view)
            ) {
                return;
            }
            loadDailyMenuItems(view);
        };
        window.addEventListener('daily-menu-template-synced', onTemplateSynced);
        return () =>
            window.removeEventListener('daily-menu-template-synced', onTemplateSynced);
    }, [activeTab, menuTypeFilter, dailyMenuViewDate, loadDailyMenuItems]);

    useEffect(() => {
        setDailyMenuViewDate(getLocalDateString());
    }, [restaurantId, selectedRestaurantForMenu]);

    useEffect(() => {
        // Fetch food categories on component mount
        const fetchFoodCategories = async () => {
            try {
                const token = localStorage.getItem('Token')
                const response = await axiosRestaurantInstance.get(RESTAURANT_API_PATHS.restaurantsFoodCategories, {
                    headers: {
                        'Authorization': `Bearer ${token}`, // Set the token in the 'Authorization' header
                    },
                    timeout: 30000 // Increase timeout to 30 seconds for this request
                });
                setFoodCategories(normalizeFoodCategories(response.data));
            } catch (error) {
                console.error('Error fetching food categories:', error);
                // Set empty array as fallback to prevent UI errors
                setFoodCategories([]);
                // Optionally show a user-friendly message
                if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                    console.warn('Food categories request timed out. Using empty categories list.');
                }
            }
        };
        fetchFoodCategories();
    }, []);

    // Reset pagination when dailyMenuItems changes
    useEffect(() => {
        setCurrentPageDailyMenu(1);
    }, [dailyMenuItems.length]);

    // Pagination handler for daily menu
    const handlePageChangeDailyMenu = (pageNumber) => {
        setCurrentPageDailyMenu(pageNumber);
    };

    const resolveDailyMenuEditIngredients = (dailyItem) => {
        const fromPascal =
            dailyItem.Ingredients != null && String(dailyItem.Ingredients).trim() !== ''
                ? String(dailyItem.Ingredients).trim()
                : '';
        if (fromPascal) return fromPascal;
        const low = dailyItem.ingredients;
        if (typeof low === 'string' && low.trim()) return low.trim();
        if (Array.isArray(low) && low.length > 0) {
            return low
                .map((ing) => ing?.ingredientName || ing?.name || '')
                .filter(Boolean)
                .join(', ');
        }
        return '';
    };

    const handleEditDailyMenuItem = (dailyItemId) => {
        const rowId = resolveDailyMenuRowId(dailyItemId);
        if (!rowId) {
            alert('Cannot edit: missing daily menu item id.');
            return;
        }
        const selectedItem = dailyMenuItems.find((item) => dailyMenuRowIdsEqual(item, rowId));
        if (selectedItem) {
            setEditingItemId(rowId);
            setTempDailyMenuItem({
                Food_Type: selectedItem.Food_Category ?? selectedItem.Food_Type ?? selectedItem.foodCategory ?? '',
                Food_Item: selectedItem.Food_Item ?? selectedItem.foodItem ?? '',
                Meal: selectedItem.Meal ?? selectedItem.meal ?? '',
                Price: selectedItem.Price ?? selectedItem.price ?? '',
                Quantity: selectedItem.Quantity ?? selectedItem.quantity ?? '',
                Notes: selectedItem.Notes ?? selectedItem.notes ?? '',
                Ingredients: resolveDailyMenuEditIngredients(selectedItem),
                No_of_items_available:
                    selectedItem.No_of_items_available ??
                    selectedItem.no_of_items_available ??
                    '',
                Food_Item_Id:
                    selectedItem.Food_Item_Id ??
                    selectedItem.foodItemId ??
                    selectedItem.food_item_id ??
                    null,
                agePricing: parseAgePricingFromItem(selectedItem),
            });
        }
    };
    const handleUpdateDailyMenuItem = async (dailyItemId) => {
        const rowId = resolveDailyMenuRowId(dailyItemId);
        console.log('Updating item with ID:', rowId);

        if (!rowId || !tempDailyMenuItem) {
            console.error('Daily item ID or temp item data is undefined. Cannot update item.');
            alert('Failed to update daily menu item: Missing data.');
            return;
        }

        const token = localStorage.getItem('Token');
        const headers = {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
        };
        const requestConfig = { headers, timeout: 5000 };

        const updatedItemData = {
            Food_Type: tempDailyMenuItem.Food_Type,
            Food_Item: tempDailyMenuItem.Food_Item,
            Meal: tempDailyMenuItem.Meal,
            Price: tempDailyMenuItem.Price,
            Quantity: tempDailyMenuItem.Quantity,
            No_of_items_available: tempDailyMenuItem.No_of_items_available,
            Notes: tempDailyMenuItem.Notes,
            Ingredients: tempDailyMenuItem.Ingredients,
        };

        try {
            const response = await axiosRestaurantInstance.put(
                RESTAURANT_API_PATHS.updateDailyMenuItemLegacy(rowId),
                updatedItemData,
                requestConfig
            );

            if (response.status === 200) {
                const foodItemId = Number(
                    tempDailyMenuItem.Food_Item_Id ?? tempDailyMenuItem.foodItemId
                );
                let nextAgePricing = Array.isArray(tempDailyMenuItem.agePricing)
                    ? tempDailyMenuItem.agePricing
                    : [];
                if (
                    foodItemId > 0 &&
                    Array.isArray(tempDailyMenuItem.agePricing) &&
                    tempDailyMenuItem.agePricing.length > 0
                ) {
                    const user = JSON.parse(localStorage.getItem('user') || 'null');
                    const actorId =
                        Number(user?.userId) ||
                        Number(selectedRestaurantForMenu || restaurantId) ||
                        1;
                    const ageRes = await replaceFoodItemAgePricing(
                        foodItemId,
                        tempDailyMenuItem.agePricing,
                        actorId
                    );
                    nextAgePricing =
                        ageRes.data?.agePricing ?? tempDailyMenuItem.agePricing;
                }

                const updatedItems = dailyMenuItems.map((item) =>
                    dailyMenuRowIdsEqual(item, rowId)
                        ? {
                              ...item,
                              ...updatedItemData,
                              agePricing: nextAgePricing,
                          }
                        : item
                );
                setDailyMenuItems(updatedItems);
                setEditingItemId(null);
                alert(response.data?.message || 'Daily menu item updated successfully!');
            } else {
                alert('Failed to update daily menu item: Unexpected response.');
            }
        } catch (error) {
            if (error.response) {
                console.error('Error response from server:', error.response.data);
                alert(`Failed to update daily menu item: ${error.response.data?.message || error.response.data?.error || 'Server error'}`);
            } else if (error.request) {
                console.error('No response received:', error.request);
                alert('Failed to update daily menu item: No response from server.');
            } else {
                console.error('Error:', error.message);
                alert(`Failed to update daily menu item: ${error.message}`);
            }
        }
    };
    
    
    const handleCloseEdit = () => {
        setEditingItemId(null); // Reset to original state without saving
    };
    
    
    const handleDeleteDailyMenuItem = async (dailyItemId) => {
        const rowId = resolveDailyMenuRowId(dailyItemId);
        if (!rowId) {
            alert('Cannot remove item: missing daily menu item id.');
            return;
        }

        const token = localStorage.getItem('Token');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };

        const existing = (dailyMenuItems || []).find((it) => dailyMenuRowIdsEqual(it, rowId));
        const itemName =
            existing?.Food_Item_Name || existing?.foodItemName || existing?.name || existing?.Food_Item || 'this item';

        const confirmed = await confirmAction({
            title: 'Remove from daily menu?',
            message: `"${itemName}" will be removed from the daily menu.`,
            confirmLabel: 'Remove',
            cancelLabel: 'Keep',
            danger: true,
        });
        if (!confirmed) return;

        const prevItems = dailyMenuItems;
        const prevAdded = addedDailyMenuItems;
        setDailyMenuItems((items) => items.filter((item) => !dailyMenuRowIdsEqual(item, rowId)));
        setAddedDailyMenuItems((state) => state.filter((item) => Number(item) !== rowId));

        showUndoToast({
            message: `Removed "${itemName}"`,
            onUndo: () => {
                setDailyMenuItems(prevItems);
                setAddedDailyMenuItems(prevAdded);
            },
            onCommit: async () => {
                try {
                    const response = await axiosRestaurantInstance.delete(
                        RESTAURANT_API_PATHS.deleteDailyMenuItemLegacy(rowId),
                        { headers }
                    );
                    if (response.status !== 200) {
                        setDailyMenuItems(prevItems);
                        setAddedDailyMenuItems(prevAdded);
                        alert('Failed to delete daily menu item.');
                        return;
                    }
                    const effectiveRestaurantId = selectedRestaurantForMenu || restaurantId;
                    markDailyMenuSkipAutoPublish(
                        effectiveRestaurantId,
                        dailyMenuViewDate || getLocalDateString(),
                        dailyMenuListSource
                    );
                    await loadDailyMenuItems(dailyMenuViewDate);
                } catch (error) {
                    console.error('Network error:', error);
                    setDailyMenuItems(prevItems);
                    setAddedDailyMenuItems(prevAdded);
                    alert(error.response?.data?.message || error.response?.data?.error || 'Network error. Unable to delete daily menu item.');
                }
            },
        });
    };
    

    const goToMainMenuView = useCallback(() => {
        setActiveTab('menu');
        setMenuSubView('menu');
        setSelectedTemplateId(null);
        setSelectedRecipeFoodItemId(null);
        setRecipePageMode('view');
        navigate(restaurantsHomeUrl(RESTAURANTS_HOME_TAB, menuChannel));
    }, [navigate, menuChannel]);

    const goToFoodItemDetail = useCallback(
        (foodItemId) => {
            if (!foodItemId) return;
            setActiveTab('menu');
            setMenuSubView('foodItemDetail');
            setSelectedRecipeFoodItemId(String(foodItemId));
            setSelectedTemplateId(null);
            setRecipePageMode('view');
            navigate(foodItemDetailTabPath(foodItemId, menuTypeFilter, menuChannel));
        },
        [navigate, menuTypeFilter, menuChannel]
    );

    const goToRecipePage = useCallback(
        (foodItemId, mode = 'view') => {
            if (!foodItemId) return;
            setActiveTab('menu');
            setMenuSubView('recipe');
            setSelectedRecipeFoodItemId(String(foodItemId));
            setRecipePageMode(mode);
            navigate(recipeTabPath(foodItemId, mode, menuTypeFilter, menuChannel));
        },
        [navigate, menuTypeFilter, menuChannel]
    );

    const goToMyTemplatesView = useCallback(() => {
        setActiveTab('menu');
        setMenuSubView('myTemplates');
        setSelectedTemplateId(null);
        setSelectedRecipeFoodItemId(null);
        setRecipePageMode('view');
        navigate(restaurantsHomeUrl(MY_TEMPLATES_HOME_TAB, menuChannel));
    }, [navigate, menuChannel]);

    useEffect(() => {
        const parsed = parseRestaurantTabFromUrl(searchParams.get('tab'));
        if (
            parsed.menuSubView === 'myTemplates' ||
            parsed.menuSubView === 'templateDetail' ||
            parsed.menuSubView === 'recipe' ||
            parsed.menuSubView === 'foodItemDetail'
        ) {
            setActiveTab('menu');
        }
        setMenuSubView(parsed.menuSubView);
        setSelectedTemplateId(parsed.templateId);
        setSelectedRecipeFoodItemId(parsed.foodItemId);
        setRecipePageMode(parsed.recipeMode);
        if (parsed.menuSubView === 'menu') {
            setSelectedTemplateId(null);
            setSelectedRecipeFoodItemId(null);
            setRecipePageMode('view');
        }
    }, [searchParams]);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setMenuSubView('menu');
        setSelectedTemplateId(null);
        setSelectedRecipeFoodItemId(null);
        setRecipePageMode('view');
        navigate(restaurantsHomeUrl(RESTAURANTS_HOME_TAB, menuChannel));
    };

    // Channel is chosen from the left sidebar (Menu sub-items) and arrives as a prop.
    useEffect(() => {
        const channel = getMenuChannel(menuChannel);
        setMenuTypeFilter(channel.type);
        setMenuListSource(channel.source);
        setDailyMenuListSource(channel.source);
        // Catering has Main Menu + Templates but no Daily Menu; bounce off Daily Menu.
        // Local state only — do not navigate (navigating to /home remounts and loops).
        if (channel.type === 'catering' && activeTab === 'dailyMenu') {
            setActiveTab('menu');
            setMenuSubView('menu');
            setSelectedTemplateId(null);
            setSelectedRecipeFoodItemId(null);
            setRecipePageMode('view');
        }
        // Jr Eats has no Main Menu — land on Daily Menu (statewide catalog is browsable there).
        if (channel.source === 'JREATS') {
            const onMainMenu =
                activeTab === 'menu' &&
                (menuSubView === 'menu' ||
                    menuSubView === 'foodItemDetail' ||
                    menuSubView === 'recipe');
            if (onMainMenu || (activeTab === 'menu' && menuSubView === 'menu')) {
                setActiveTab('dailyMenu');
                setMenuSubView('menu');
                setSelectedTemplateId(null);
                setSelectedRecipeFoodItemId(null);
                setRecipePageMode('view');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [menuChannel]);

    // Keep Jr Eats off the Main Menu view if URL/tab still points there.
    useEffect(() => {
        if (menuListSource !== 'JREATS') return;
        if (activeTab === 'menu' && menuSubView === 'menu') {
            setActiveTab('dailyMenu');
        }
    }, [menuListSource, activeTab, menuSubView]);

    const handleAddMenu = (e) => {
        e.preventDefault(); // Prevent the default form submission
        if (menuListSource === 'JREATS') {
            return;
        }
        const isJreats = menuListSource === 'JREATS';
        const isDinein = menuListSource === 'DINEIN';
        setNewMenuItem((prev) => ({
            ...prev,
            menuSource: isDinein ? 'MYEATS' : menuListSource,
            availableForDineIn: !isJreats,
            availableForOnline: isDinein ? false : !isJreats,
        }));
        setShowAddMenu(true);
    };

  const handleAddDailyMenu = () => {
    setShowMenuTable(true); // Display the table when "Add Daily Menu" is clicked
  };

    const handleCloseAddMenu = (e) => {
        e.preventDefault(); // Prevent the default form submission
        setShowAddMenu(false);
        setNewMenuItem({
            foodType: '',
            foodCategoryId: '',
            itemFoodType: 'VEG',
            foodItem: '',
            price: '',
            quantity: '',
            notes: '',
            ingredients: '',
            ingredientIds: [],
            cuisineOrigin: '',
            description: '',
            currency: 'INR',
            nutrition: emptyNutritionRows(),
            menuSource: 'MYEATS',
            availableForDineIn: true,
            availableForOnline: true,
            menuType: 'ONLINE',
            isAvailable: true,
            imageFile: null,
            ...EMPTY_GROUP_SCALE_FIELDS,
        });
    };

    const handleInputChange = (e) => {
        if (e.target.type === 'file') {
            setNewMenuItem((prev) => ({ ...prev, imageFile: e.target.files?.[0] ?? null }));
        } else if (e.target.type === 'ingredient-select') {
            setNewMenuItem((prev) => ({
                ...prev,
                ingredientIds: Array.isArray(e.target.value) ? e.target.value : [],
                ingredients: e.target.ingredients ?? '',
            }));
        } else {
            const { name, value } = e.target;
            if (name === 'isAvailable') {
                setNewMenuItem((prev) => ({ ...prev, isAvailable: value === '1' }));
            } else if (e.target.type === 'checkbox') {
                const nextChecked =
                    typeof e.target.checked === 'boolean'
                        ? e.target.checked
                        : e.target.value === true ||
                          e.target.value === 'true' ||
                          e.target.value === '1';
                setNewMenuItem((prev) => ({ ...prev, [name]: nextChecked }));
            } else if (name === 'foodType' && menuTypeFilter !== 'catering') {
                setNewMenuItem((prev) => ({
                    ...prev,
                    foodType: value,
                    foodCategory: value,
                }));
            } else {
                setNewMenuItem((prev) => ({ ...prev, [name]: value }));
            }
        }
    };

    const handleAddNewItem = async (e) => {
        e.preventDefault(); // Prevent default form submission
    
        const isCatering = menuTypeFilter === 'catering';

        const currentRestaurantId = isCatering
            ? restaurantId
            : (selectedRestaurantForMenu || restaurantId);
        const categoryOk = isCatering
            ? newMenuItem.foodCategoryId
            : (newMenuItem.foodCategory || newMenuItem.foodType);
        const quantityOk = String(newMenuItem.quantity ?? '').trim();
        if (
            !currentRestaurantId ||
            !categoryOk ||
            !newMenuItem.foodItem ||
            !newMenuItem.price ||
            !quantityOk
        ) {
            alert(
                'Please fill in all required fields: Food Category, Food Item, Price, and Quantity.'
            );
            return;
        }

        const token = localStorage.getItem('Token');
        const createdById = user?.userId || currentRestaurantId || 1;

        try {
            let payload;
            let endpoint;

            if (isCatering) {
                endpoint = '/catering/menu';
                // POST /catering/menu — form-data: restaurantId, foodCategoryId, foodType (diet), itemName, price, quantity, isAvailable, createdBy, image (optional), description
                const formData = new FormData();
                formData.append('restaurantId', String(currentRestaurantId));
                formData.append('foodCategoryId', String(newMenuItem.foodCategoryId));
                formData.append('foodType', newMenuItem.itemFoodType || 'VEG');
                formData.append('itemName', newMenuItem.foodItem);
                formData.append('price', String(newMenuItem.price ?? '').trim());
                formData.append('quantity', String(newMenuItem.quantity ?? 'single plate serves 1').trim() || 'single plate serves 1');
                formData.append(
                    'isAvailable',
                    newMenuItem.isAvailable === false || newMenuItem.isAvailable === '0' ? '0' : '1'
                );
                formData.append('createdBy', String(createdById));
                if (newMenuItem.description != null && String(newMenuItem.description).trim() !== '') {
                    formData.append('description', String(newMenuItem.description).trim());
                }
                if (newMenuItem.imageFile) {
                    const imageFile = await ensureMenuImageFile(newMenuItem.imageFile);
                    formData.append('image', imageFile, imageFile.name ?? 'menu-image.webp');
                }
                payload = formData;
            } else {
                const response = await createOnlineFoodItem(
                    currentRestaurantId,
                    {
                        ...newMenuItem,
                        menuSource: menuListSource === 'JREATS' ? 'JREATS' : 'MYEATS',
                    },
                    createdById,
                    { foodCategories }
                );
                if (response.status === 201 || response.status === 200) {
                    try {
                        await reloadOnlineMenuItems(currentRestaurantId);
                        setCateringMenuItems({});
                    } catch (error) {
                        console.error('Error reloading menu items:', error);
                        const foodItemId =
                            response.data?.foodItemId ??
                            response.data?.Food_Item_Id ??
                            response.data?.food_item_id;
                        if (foodItemId) {
                            const newItem = {
                                Food_Item_Id: foodItemId,
                                Food_Type: newMenuItem.foodType,
                                foodCategory: newMenuItem.foodType,
                                itemFoodType: newMenuItem.itemFoodType || 'VEG',
                                Food_Item: newMenuItem.foodItem,
                                Price: newMenuItem.price,
                                Quantity: newMenuItem.quantity,
                                Description: newMenuItem.description,
                                cuisineOrigin: newMenuItem.cuisineOrigin,
                                Nutrition: newMenuItem.nutrition,
                                nutrition: newMenuItem.nutrition,
                                Menu_Source: newMenuItem.menuSource || 'MYEATS',
                                Currency: newMenuItem.currency || 'INR',
                                imagePath: response.data?.imagePath || null,
                                imageUrl: response.data?.imageUrl || undefined,
                            };
                            setMenuItems((prevItems) => ({
                                ...prevItems,
                                [newItem.Food_Type]: [...(prevItems[newItem.Food_Type] || []), newItem],
                            }));
                        }
                    }
                    setShowAddMenu(false);
                    setNewMenuItem({
                        foodType: '',
                        foodCategoryId: '',
                        itemFoodType: 'VEG',
                        foodItem: '',
                        price: '',
                        quantity: '',
                        notes: '',
                        ingredients: '',
                        ingredientIds: [],
                        cuisineOrigin: '',
                        description: '',
                        currency: 'INR',
                        nutrition: emptyNutritionRows(),
                        menuSource: 'MYEATS',
                        availableForDineIn: true,
                        availableForOnline: true,
                        menuType: 'ONLINE',
                        isAvailable: true,
                        imageFile: null,
                        ...EMPTY_GROUP_SCALE_FIELDS,
                    });
                    alert(response.data?.message || 'Menu item added successfully!');
                }
                return;
            }

            const postConfig = {
                headers: token ? { Authorization: `Bearer ${token}` } : {},
            };
            if (payload instanceof FormData) {
                postConfig.headers['Content-Type'] = false; // Let browser set multipart/form-data with boundary
            }
            const response = await axiosRestaurantInstance.post(endpoint, payload, postConfig);
    
            if (response.status === 201 || response.status === 200) {
                // Reload menu items
                try {
                    if (isCatering) {
                        try {
                            const cateringResponse = await axiosRestaurantInstance.get(`/catering/menu/${currentRestaurantId}`, {
                                headers: { 'Authorization': `Bearer ${token}` },
                            });
                            const cateringData = cateringResponse.data || {};
                            const cateringItems = parseCateringMenuResponse(cateringData);
                            const grouped = {};
                            cateringItems.forEach((item) => {
                                const cat = item.foodCategory ?? item.foodCategoryName ?? item.Food_Category ?? item.Food_Type ?? 'Other';
                                if (!grouped[cat]) grouped[cat] = [];
                                grouped[cat].push(mapCateringMenuItem(item));
                            });
                            setCateringMenuItems(grouped);
                        } catch (fetchErr) {
                            const categoryName =
                                foodCategories.find(
                                    (cat) =>
                                        String(cat.food_category_id ?? cat.foodCategoryId ?? cat.id) ===
                                        String(newMenuItem.foodCategoryId)
                                )?.food_category_name ??
                                foodCategories.find(
                                    (cat) =>
                                        String(cat.food_category_id ?? cat.foodCategoryId ?? cat.id) ===
                                        String(newMenuItem.foodCategoryId)
                                )?.foodCategoryName ??
                                'Other';
                            const newItem = {
                                Food_Item_Id: response.data?.cateringItemId,
                                cateringItemId: response.data?.cateringItemId,
                                foodCategoryId: newMenuItem.foodCategoryId,
                                foodCategory: categoryName,
                                Food_Category: categoryName,
                                Food_Type: categoryName,
                                itemFoodType: newMenuItem.itemFoodType || 'VEG',
                                Food_Item: newMenuItem.foodItem,
                                Price: newMenuItem.price,
                                Quantity: newMenuItem.quantity || 'serves 1',
                                Description: newMenuItem.description ?? '',
                                imagePath: response.data?.imagePath ?? null,
                                imageUrl: response.data?.imageUrl ?? null,
                            };
                            setCateringMenuItems((prev) => ({
                                ...prev,
                                [categoryName]: [...(prev[categoryName] || []), newItem],
                            }));
                        }
                        setMenuItems({});
                    } else {
                        await reloadOnlineMenuItems(currentRestaurantId);
                        setCateringMenuItems({});
                    }
                } catch (error) {
                    console.error('Error reloading menu items:', error);
                    // If reload fails, try to construct the item from response
                    if (response.data && (response.data.Food_Item_Id || response.data.food_item_id)) {
                        const foodItemId = response.data.Food_Item_Id || response.data.food_item_id;
                        const newItem = {
                            Food_Item_Id: foodItemId,
                            Food_Type: newMenuItem.foodType,
                            Food_Item: newMenuItem.foodItem,
                            Price: newMenuItem.price,
                            Quantity: newMenuItem.quantity,
                            Notes: newMenuItem.notes,
                            Nutrition: newMenuItem.nutrition,
                            nutrition: newMenuItem.nutrition,
                            Menu_Source: newMenuItem.menuSource || 'MYEATS',
                            ingredients: [],
                            imagePath: response.data.imagePath || null,
                            imageUrl: response.data.imageUrl || undefined,
                        };

                        setMenuItems((prevItems) => ({
                            ...prevItems,
                            [newItem.Food_Type]: [...(prevItems[newItem.Food_Type] || []), newItem],
                        }));
                    }
                }

                setShowAddMenu(false);
                setNewMenuItem({
                    foodType: '',
                    foodCategoryId: '',
                    itemFoodType: 'VEG',
                    foodItem: '',
                    price: '',
                    quantity: '',
                    notes: '',
                    ingredients: '',
                    ingredientIds: [],
                    cuisineOrigin: '',
                    description: '',
                    currency: 'INR',
                    nutrition: emptyNutritionRows(),
                    menuSource: 'MYEATS',
                    availableForDineIn: true,
                    availableForOnline: true,
                    menuType: 'ONLINE',
                    isAvailable: true,
                    imageFile: null,
                    ...EMPTY_GROUP_SCALE_FIELDS,
                });
                alert(
                    isCatering
                        ? (response.data?.message || 'Catering menu item added successfully')
                        : (response.data?.message || 'Menu item added successfully!')
                );
            }
        } catch (error) {
            // Handle errors
            if (error.response) {
                console.error('Error data:', error.response.data);
                console.error('Error status:', error.response.status);
                alert(`Error: ${error.response.data.error || error.response.data.message || 'An error occurred while adding the item.'}`);
            } else if (error.request) {
                console.error('Request data:', error.request);
                alert('Error: No response received from the server.');
            } else {
                console.error('Error message:', error.message);
                alert(`Error: ${error.message}`);
            }
        }
    };    

    const handleEditClick = (item) => {
        if (menuTypeFilter === 'online') {
            const ingredientIds = resolveIngredientIdsFromMenuItem(item);
            setEditingItem({
                ...item,
                ingredientIds,
                ingredients: Array.isArray(item.ingredients) ? [...item.ingredients] : item.ingredients,
            });
            return;
        }
        setEditingItem(item);
    };

    const handleUpdateItem = async (item) => {
        console.log('Item passed to handleUpdateItem:', item);
    
        try {
            if (!item.Food_Item_Id && !item.Daily_Item_Id) {
                console.error('Missing Food_Item_Id / Daily_Item_Id for the item.', item);
                alert('Error: Missing item ID.');
                return;
            }

            if (String(item.quantity ?? item.Quantity ?? '').trim() === '') {
                alert('Please enter the quantity (serving size) for this item.');
                return;
            }

            const token = localStorage.getItem('Token');

            // CATERING menu: PUT /catering/menu-item/:id — form-data: restaurantId, foodCategoryId, foodType (diet), itemName, price, quantity, isAvailable, updatedBy, image (optional), description
            if (menuTypeFilter === 'catering') {
                const currentRestaurantId = restaurantId ?? item.restaurantId;
                const updatedById = user?.userId || currentRestaurantId || 1;
                const formData = new FormData();
                formData.append('restaurantId', String(currentRestaurantId));
                formData.append(
                    'foodCategoryId',
                    String(item.foodCategoryId ?? item.Food_Category_Id ?? '')
                );
                formData.append('foodType', item.itemFoodType ?? item.foodType ?? 'VEG');
                formData.append('itemName', item.foodItem ?? item.Food_Item);
                formData.append('price', String(item.price ?? item.Price ?? '').trim());
                formData.append('quantity', String(item.quantity ?? item.Quantity ?? item.Serving_Size ?? 'single plate serves 1').trim() || 'single plate serves 1');
                const isAvailVal = item.isAvailable ?? item.Is_available ?? item.is_available;
                formData.append(
                    'isAvailable',
                    isAvailVal === false || isAvailVal === 0 || isAvailVal === '0' ? '0' : '1'
                );
                formData.append('updatedBy', String(updatedById));
                const descriptionVal = item.description ?? item.Description;
                if (descriptionVal != null && String(descriptionVal).trim() !== '') {
                    formData.append('description', String(descriptionVal).trim());
                }
                if (item.imageFile) {
                    const imageFile = await ensureMenuImageFile(item.imageFile);
                    formData.append('image', imageFile, imageFile.name ?? 'menu-image.webp');
                }

                const putConfig = {
                    headers: { Authorization: `Bearer ${token}` },
                    transformRequest: [(data, headers) => {
                        delete headers['Content-Type'];
                        return data;
                    }],
                };

                const putResponse = await axiosRestaurantInstance.put(`/catering/menu-item/${item.Food_Item_Id}`, formData, putConfig);
                const cateringResponse = await axiosRestaurantInstance.get(`/catering/menu/${currentRestaurantId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const cateringData = cateringResponse.data || {};
                const cateringItems = parseCateringMenuResponse(cateringData);
                const grouped = {};
                cateringItems.forEach((i) => {
                    const cat = i.foodCategory ?? i.foodCategoryName ?? i.Food_Category ?? i.Food_Type ?? 'Other';
                    if (!grouped[cat]) grouped[cat] = [];
                    grouped[cat].push(mapCateringMenuItem(i));
                });
                setCateringMenuItems(grouped);
                setEditingItem(null);
                alert(putResponse.data?.message || 'Catering menu item updated successfully');
                return;
            }

            const updatedById = user?.userId || item.restaurantId || restaurantId;
            const response = await updateOnlineFoodItem(
                item.Food_Item_Id,
                item.restaurantId ?? restaurantId,
                item,
                updatedById,
                { foodCategories }
            );

            if (response.status === 200 || response.status === 201) {
                try {
                    await reloadOnlineMenuItems(item.restaurantId ?? restaurantId);
                } catch (error) {
                    console.error('Error reloading menu items:', error);
                    setMenuItems((prevItems) => {
                        const updatedMenuItems = { ...prevItems };
                        const imagePath = response.data?.imagePath ?? response.data?.image_path;
                        const nextIngredientIds = (
                            response.data?.ingredient_ids ??
                            response.data?.Ingredient_Ids ??
                            item.ingredientIds ??
                            []
                        )
                            .map((id) => Number(id))
                            .filter((id) => Number.isFinite(id));
                        Object.keys(updatedMenuItems).forEach((foodType) => {
                            updatedMenuItems[foodType] = updatedMenuItems[foodType].map((menuItem) => {
                                if (menuItem.Food_Item_Id === item.Food_Item_Id) {
                                    const previousIngredients = Array.isArray(menuItem.ingredients)
                                        ? menuItem.ingredients
                                        : [];
                                    const nextIngredients = nextIngredientIds.map((id) => {
                                        const existing = previousIngredients.find(
                                            (ing) => Number(ing.ingredientId) === id
                                        );
                                        return (
                                            existing ?? {
                                                ingredientId: id,
                                                ingredientName: '',
                                            }
                                        );
                                    });
                                    const nextStatus =
                                        item.status ?? item.Status ?? menuItem.Status ?? menuItem.status;
                                    return {
                                        ...menuItem,
                                        Food_Item: item.foodItem ?? item.Food_Item,
                                        Food_Type: item.foodType ?? item.Food_Type ?? item.foodCategory,
                                        foodCategory: item.foodCategory ?? item.foodType ?? item.Food_Type,
                                        itemFoodType: item.itemFoodType ?? item.foodType,
                                        Price: item.price ?? item.Price,
                                        Quantity: item.quantity ?? item.Quantity,
                                        Description: item.description ?? item.Description,
                                        cuisineOrigin: item.cuisineOrigin ?? item.Cuisine_Origin,
                                        Currency: item.currency ?? item.Currency ?? 'INR',
                                        Status: nextStatus,
                                        status: nextStatus,
                                        Nutrition: item.nutrition ?? item.Nutrition ?? menuItem.Nutrition,
                                        nutrition: item.nutrition ?? item.Nutrition ?? menuItem.nutrition,
                                        Menu_Source:
                                            item.menuSource ??
                                            item.Menu_Source ??
                                            menuItem.Menu_Source ??
                                            'MYEATS',
                                        ingredientIds: nextIngredientIds,
                                        ingredients: nextIngredients,
                                        ...(imagePath != null && { imagePath }),
                                        ...(response.data?.imageUrl != null && {
                                            imageUrl: response.data.imageUrl,
                                        }),
                                    };
                                }
                                return menuItem;
                            });
                        });
                        return updatedMenuItems;
                    });
                }

                setEditingItem(null);
                alert(response.data?.message || 'Food item updated successfully!');
            }
        } catch (error) {
            console.error(
                'Error updating food item:',
                error.response ? error.response.data : error.message
            );
            alert(
                `Error: ${
                    error.response
                        ? error.response.data
                        : 'Unable to update item. Please try again.'
                }`
            );
        }
    };


    const findMenuItemNameById = (foodItemId, source) => {
        const targetId = Number(foodItemId);
        let name = '';
        Object.values(source || {}).forEach((arr) => {
            (arr || []).forEach((it) => {
                if (Number(it.Food_Item_Id) === targetId) {
                    name = it.Food_Item_Name || it.foodItemName || it.name || '';
                }
            });
        });
        return name;
    };

    const handleDeleteMenuItem = async (foodItemId) => {
        const token = localStorage.getItem('Token');
        const headers = {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
        };
        const isCatering = menuTypeFilter === 'catering';

        if (!isCatering && menuTypeFilter !== 'online') {
            alert('Deleting menu items is currently supported only for Online menu type.');
            return;
        }

        const source = isCatering ? cateringMenuItems : menuItems;
        const itemName = findMenuItemNameById(foodItemId, source) || 'this item';

        const confirmed = await confirmAction({
            title: 'Delete menu item?',
            message: `"${itemName}" will be removed from the menu.`,
            confirmLabel: 'Delete',
            cancelLabel: 'Keep',
            danger: true,
        });
        if (!confirmed) return;

        const targetId = Number(foodItemId);

        if (isCatering) {
            const prevCatering = cateringMenuItems;
            const updatedCatering = { ...prevCatering };
            Object.keys(updatedCatering).forEach((foodType) => {
                updatedCatering[foodType] = updatedCatering[foodType].filter(
                    (item) => Number(item.Food_Item_Id) !== targetId
                );
                if (updatedCatering[foodType].length === 0) delete updatedCatering[foodType];
            });
            setCateringMenuItems(updatedCatering);

            showUndoToast({
                message: `Deleted "${itemName}"`,
                onUndo: () => setCateringMenuItems(prevCatering),
                onCommit: async () => {
                    try {
                        const response = await axiosRestaurantInstance.delete(
                            `/catering/menu-items/${foodItemId}`,
                            { headers }
                        );
                        if (response.status !== 200) {
                            setCateringMenuItems(prevCatering);
                            alert('Failed to delete the catering menu item.');
                        }
                    } catch (error) {
                        console.error('Network error:', error);
                        setCateringMenuItems(prevCatering);
                        alert(error.response?.data?.message || 'Network error. Unable to delete menu item.');
                    }
                },
            });
            return;
        }

        const currentRestaurantId = selectedRestaurantForMenu || restaurantId;
        const prevMenuItems = menuItems;
        setMenuItems((prevItems) => {
            const updated = { ...prevItems };
            Object.keys(updated).forEach((foodType) => {
                updated[foodType] = updated[foodType].filter(
                    (item) => Number(item.Food_Item_Id) !== targetId
                );
            });
            return updated;
        });

        showUndoToast({
            message: `Deleted "${itemName}"`,
            onUndo: () => setMenuItems(prevMenuItems),
            onCommit: async () => {
                try {
                    const response = await axiosRestaurantInstance.delete(
                        RESTAURANT_API_PATHS.deleteFoodItemById(foodItemId),
                        { headers }
                    );
                    if (response.status === 200) {
                        try {
                            await reloadOnlineMenuItems(currentRestaurantId);
                        } catch (reloadError) {
                            console.error('Error reloading menu items after deactivate:', reloadError);
                        }
                    } else {
                        setMenuItems(prevMenuItems);
                        alert(response.data?.message || 'Failed to delete the food item.');
                    }
                } catch (error) {
                    console.error('Network error:', error);
                    setMenuItems(prevMenuItems);
                    alert(error.response?.data?.message || 'Network error. Unable to delete menu item.');
                }
            },
        });
    };
    
    const handleCancelEdit = () => {
        setEditingItem(null);
    };

    return (
<div className="super-container">
        
    <div className="form-container visible">

                    {/* Menu navigation: Main Menu / Templates / Daily Menu */}
                    {(() => {
                        const isJreats = menuListSource === 'JREATS';
                        const isTemplatesActive =
                            activeTab === 'menu' &&
                            (menuSubView === 'myTemplates' || menuSubView === 'templateDetail');
                        const isDailyActive = activeTab === 'dailyMenu';
                        const isMainActive = !isJreats && !isTemplatesActive && !isDailyActive;
                        const isCatering = menuTypeFilter === 'catering';
                        return (
                            <div className="menu-nav">
                                <div className="menu-nav__tabs menu-nav__tabs--underline" role="tablist" aria-label="Menu view">
                                    {!isJreats && (
                                        <button
                                            type="button"
                                            className={`menu-nav__tab ${isMainActive ? 'is-active' : ''}`}
                                            onClick={goToMainMenuView}
                                        >
                                            <FaListUl className="menu-nav__tab-icon" aria-hidden="true" />
                                            <span>Main Menu</span>
                                        </button>
                                    )}
                                    <button
                                        type="button"
                                        className={`menu-nav__tab ${isTemplatesActive ? 'is-active' : ''}`}
                                        onClick={goToMyTemplatesView}
                                    >
                                        <FaClipboardList className="menu-nav__tab-icon" aria-hidden="true" />
                                        <span>{isCatering ? 'Packages' : 'Templates'}</span>
                                    </button>
                                    {!isCatering && (
                                        <button
                                            type="button"
                                            className={`menu-nav__tab ${isDailyActive ? 'is-active' : ''}`}
                                            onClick={() => handleTabChange('dailyMenu')}
                                        >
                                            <FaCalendarAlt className="menu-nav__tab-icon" aria-hidden="true" />
                                            <span>Daily Menu</span>
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Tab content */}
                    <div className="tab-content">
                {activeTab === 'menu' && menuSubView === 'myTemplates' && (
                    (menuTypeFilter === 'online' || menuTypeFilter === 'catering') && restaurantId ? (
                        menuTypeFilter === 'catering' ? (
                            <CateringTemplate
                                key={menuChannel}
                                restaurantId={restaurantId}
                                hideTitle
                            />
                        ) : (
                            <MenuTemplate
                                key={menuChannel}
                                restaurantId={restaurantId}
                                hideTitle
                                lockedSource={menuListSource}
                            />
                        )
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No restaurant selected. Please load or add a restaurant first.
                        </div>
                    )
                )}
                {activeTab === 'menu' && menuSubView === 'templateDetail' && (
                    restaurantId && selectedTemplateId ? (
                        menuTypeFilter === 'catering' ? (
                            <CateringTemplateDetail
                                cateringTempId={selectedTemplateId}
                                embedded
                            />
                        ) : menuTypeFilter === 'online' ? (
                            <MenuTemplateDetail
                                templateId={selectedTemplateId}
                                embedded
                            />
                        ) : (
                            <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                                Package details are not available for this menu type.
                            </div>
                        )
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No restaurant selected. Please load or add a restaurant first.
                        </div>
                    )
                )}
                {activeTab === 'menu' && menuSubView === 'foodItemDetail' && (
                    restaurantId && selectedRecipeFoodItemId ? (
                        <FoodItemDetailPage
                            foodItemId={selectedRecipeFoodItemId}
                            menuTypeFilter={menuTypeFilter}
                            menuSource={menuListSource}
                            restaurantId={restaurantId}
                            menuItems={
                                menuTypeFilter === 'catering' ? cateringMenuItems : menuItems
                            }
                            onBack={
                                menuListSource === 'JREATS' ? goToMyTemplatesView : goToMainMenuView
                            }
                            onRecipeNavigate={(id) => goToRecipePage(id, 'view')}
                            embedded
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No restaurant selected. Please load or add a restaurant first.
                        </div>
                    )
                )}
                {activeTab === 'menu' && menuSubView === 'recipe' && (
                    restaurantId && selectedRecipeFoodItemId ? (
                        <FoodItemRecipePage
                            foodItemId={selectedRecipeFoodItemId}
                            recipeMode={recipePageMode}
                            menuTypeFilter={menuTypeFilter}
                            restaurantId={restaurantId}
                            menuItems={
                                menuTypeFilter === 'catering' ? cateringMenuItems : menuItems
                            }
                            onBack={
                                menuListSource === 'JREATS' ? goToMyTemplatesView : goToMainMenuView
                            }
                            onModeChange={(mode) =>
                                goToRecipePage(selectedRecipeFoodItemId, mode)
                            }
                            embedded
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            No restaurant selected. Please load or add a restaurant first.
                        </div>
                    )
                )}
                {activeTab === 'menu' && menuSubView === 'menu' && menuListSource !== 'JREATS' && (
                    ((menuTypeFilter === 'online' || menuTypeFilter === 'catering') && restaurantId) ? (
                        <Menu
                            showAddMenu={showAddMenu}
                            newMenuItem={newMenuItem}
                            handleInputChange={handleInputChange}
                            handleAddNewItem={handleAddNewItem}
                            handleCloseAddMenu={handleCloseAddMenu}
                            menuItems={menuTypeFilter === 'catering' ? cateringMenuItems : menuItems}
                            setMenuItems={menuTypeFilter === 'catering' ? setCateringMenuItems : setMenuItems}
                            editingItem={editingItem}
                            handleEditClick={handleEditClick}
                            handleDeleteMenuItem={handleDeleteMenuItem}
                            handleUpdateItem={handleUpdateItem}
                            handleCancelEdit={handleCancelEdit}
                            foodCategories={foodCategories}
                            restaurantId={restaurantId}
                            handleAddMenu={handleAddMenu}
                            menuTypeFilter={menuTypeFilter}
                            menuSource={menuListSource}
                            salesChannel={menuSalesChannel}
                            onRecipeNavigate={goToRecipePage}
                            onFoodItemNavigate={goToFoodItemDetail}
                        />
                    ) : (
                        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                            {menuTypeFilter === 'online' 
                                ? 'No restaurant selected. Please load or add a restaurant first.' 
                                : 'No restaurant selected. Please load or add a restaurant first.'}
                        </div>
                    )
                )}
{activeTab === 'dailyMenu' && (
    (menuTypeFilter === 'online' && restaurantId) ? (
        <DailyMenu
            dailyMenuItems={dailyMenuItems}
            handleAddDailyMenu={handleAddDailyMenu}
            editingItemId={editingItemId}
            tempDailyMenuItem={tempDailyMenuItem}
            setTempDailyMenuItem={setTempDailyMenuItem}
            handleUpdateDailyMenuItem={handleUpdateDailyMenuItem}
            handleCloseEdit={handleCloseEdit}
            handleEditDailyMenuItem={handleEditDailyMenuItem}
            handleDeleteDailyMenuItem={handleDeleteDailyMenuItem}
            showMenuTable={showMenuTable}
            menuItems={menuItems}
            addedDailyMenuItems={addedDailyMenuItems}
            handleBulkAddDailyMenuItems={handleBulkAddDailyMenuItems}
            closeAddItemModal={closeAddItemModal}
            restaurantId={restaurantId}
            currentPage={currentPageDailyMenu}
            itemsPerPage={itemsPerPageDailyMenu}
            onPageChange={handlePageChangeDailyMenu}
            menuTypeFilter={menuTypeFilter}
            viewMenuDate={dailyMenuViewDate}
            onViewMenuDateChange={handleViewMenuDateChange}
            onRefreshDailyMenu={loadDailyMenuItems}
            dailyMenuSourceFilter={dailyMenuListSource}
        />
    ) : (
        <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
            {menuTypeFilter === 'online' 
                ? 'Please select a restaurant to view the daily menu' 
                : 'Please select a restaurant to view the daily menu'}
        </div>
    )
)}


                    </div>
            </div>
        </div>
    );
};

export default SupRestaurant;
